import {
  hashKey,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";

import type { CommunityListInput, CommunitySetHiddenInput } from "@/api/contracts/communities";
import {
  communityDetails,
  communityList,
  communityMapData,
  communitySearch,
  communitySetHidden,
} from "@/api/functions/communities";
import {
  communityDetailsQueryKey,
  communityDetailsQueryKeyPrefix,
} from "@/hooks/community-details-key";
import { SOURCE_CACHE_LIFETIME } from "@/hooks/source-cache-lifetime";
import { processSearchTerm } from "@/lib/community-id-search";

// The community reads' queries: the Community ID field's options (#708), the
// communities list (#744) and one community's details and map data (#745),
// below.
//
// The Community ID field's options (#708): one query key per term sent.
//
// - **The processed term is the key** — a named parity break (#712 D5,
//   owner-ruled 2026-09-29). Timmy's `useCommunitiesAutocompleteFalconQuery({
//   communityId })` caches by the raw debounced value and processes it only
//   inside `query` (`communitiesApi.ts`), so `7017751000` and `70177510000`,
//   one request's worth of term, are two cache entries there, each fetched,
//   spinner and all. Here they are one: the second spelling answers from the
//   cache. The key holds the term already processed, and the request sends
//   that term as it is (`processSearchTerm` is not idempotent).
// - **`keepPreviousData`.** A new value is a new key, whose default is
//   `pending` with no data, which would blank the list mid-typing. RTK Query
//   keeps the last answer while the next argument loads, and so does this — so
//   `isFetching` is the field's spinner over the old options. (What RTK also
//   keeps through a *failed* argument is the field's to hold: TanStack drops
//   the placeholder on error.)
// - **Thirty seconds once unobserved**, the source's `keepUnusedDataFor: 30`
//   and the one RTK Query default that query overrides; the rest are RTK
//   Query's, as `SOURCE_CACHE_LIFETIME` spells them — never stale while
//   cached, no focus or reconnect refetch.

/** What every community-search key starts with, exported as a value so a
 *  second spelling cannot quietly stop matching the keys it is meant to reach. */
export const communitySearchQueryKeyPrefix = ["communities", "search"] as const;

/** The key for one processed term — what `processSearchTerm` makes of a value. */
export const communitySearchQueryKey = (term: string) =>
  [...communitySearchQueryKeyPrefix, term] as const;

/** `keepUnusedDataFor: 30` (`communitiesApi.ts`). */
const COMMUNITY_SEARCH_CACHE_LIFETIME_MS = 30_000;

export const communitySearchQueryOptions = (value: string) => {
  const term = processSearchTerm(value);
  return queryOptions({
    queryKey: communitySearchQueryKey(term),
    queryFn: () => communitySearch({ data: term === "" ? {} : { search: term } }),
    placeholderData: keepPreviousData,
    ...SOURCE_CACHE_LIFETIME,
    gcTime: COMMUNITY_SEARCH_CACHE_LIFETIME_MS,
  });
};

/** The communities matching the field's value — its debounced form value. */
export function useCommunitySearch(value: string) {
  return useQuery(communitySearchQueryOptions(value));
}

// The communities list (#744): one query key per list request (ADR-0014
// decision 2).
//
// - **The whole input is the key**, under the exported
//   `communityListQueryKeyPrefix`: hide/unhide (#752, below) answers with the
//   corrected page and writes it into the key of the list query it named
//   (through `communityListQueryKey`), rather than refetching.
// - **The term is keyed processed and sent raw.** Timmy builds its RTK Query
//   argument from `processSearchTerm(debouncedSearch)`
//   (`CommunityListIsland.tsx:287-306`), so spellings that process alike — a
//   trailing space, a full id and its composite — are one cached argument
//   there: no request, no skeleton. The key holds the term processed by the
//   field's port (`@/lib/community-id-search`, pinned to Python's by shared
//   vectors) to keep that. The request still sends the raw term, because
//   Python processes it once and `processSearchTerm` is not idempotent.
// - **`keepPreviousData`, and RTK Query's defaults** (`SOURCE_CACHE_LIFETIME`):
//   the source's `getCommunitiesFalcon` overrides none of them
//   (`communitiesApi.ts:60-88`), the cache ruling the Read side took (#697 D8).
//
// The source's two waiting states (`CommunityListIsland.tsx:309-310`), in
// TanStack's words, for the page (#750):
//
// - its **loading** — `isLoading || (isFetching && !currentData)`, so skeleton
//   rows on every argument change — is `isPending || isPlaceholderData`: a new
//   key shows the previous page as placeholder data until its own answer lands;
// - its **refetching** — `isFetching && !isLoading && !!currentData`, the 24 px
//   "checking for updates" spinner — is `isFetching && !isPlaceholderData` with
//   data present: the same key fetching again over its own answer.

/** What every communities-list key starts with — hide/unhide's write target. */
export const communityListQueryKeyPrefix = ["communities", "list"] as const;

/** The key for one list request: its whole input, the term processed — and
 *  absent when it processes to nothing, as Python reads it. */
export const communityListQueryKey = (input: CommunityListInput) => {
  const { search, ...rest } = input;
  const term = processSearchTerm(search ?? "");
  return [...communityListQueryKeyPrefix, term === "" ? rest : { ...rest, search: term }] as const;
};

export const communityListQueryOptions = (input: CommunityListInput) =>
  queryOptions({
    queryKey: communityListQueryKey(input),
    queryFn: () => communityList({ data: input }),
    placeholderData: keepPreviousData,
    ...SOURCE_CACHE_LIFETIME,
  });

/** One page of the communities list, as `communityListQuery` maps the page's
 *  URL, division and sort (`@/api/contracts/communities`). `enabled` is the
 *  page's hold: no request goes out until the remembered division has been
 *  read and judged (`useEffectiveDivision`), as the Spec list's waits. */
export function useCommunityList(
  input: CommunityListInput,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({ ...communityListQueryOptions(input), enabled });
}

/**
 * One page of the communities list as the page renders it, and one row of it —
 * the server function's own answer, so the client graph names the shape without
 * importing the wire face (`client/docs/architecture.md`, "The dependency
 * rule"); the `SpecListRow` precedent.
 */
export type CommunityListPage = Awaited<ReturnType<typeof communityList>>;
export type CommunityListRow = CommunityListPage["rows"][number];

// Hide/unhide (#752), the list's one write — the #707 lifecycle verbs' shape
// (ADR-0014 decisions 4 and 5):
//
// - **The answer is the corrected page** of the list query the request named,
//   written straight into that query's key — after cancelling a read of it
//   already on the wire, which would otherwise land after and put the
//   pre-write page back — with no refetch and no loader. Every other list
//   entry (another filter, another page) is marked stale, not awaited, so the
//   next view of it reads afresh.
// - **Nothing else**: the details read carries no hidden flag, and the
//   community search filters no hidden rows.
// - **No `onMutate`**: the source's toggle is not optimistic
//   (`communitiesApi.ts:220-244`), so a refusal touches no cache.
//
// `listQuery` must be the page's own list input — the object it hands
// `useCommunityList` — so the written key is the one on screen.

export function setCommunityHiddenMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<CommunityListPage, Error, CommunitySetHiddenInput> {
  return {
    mutationFn: (data) => communitySetHidden({ data }),
    onSuccess: async (page, { listQuery }) => {
      const key = communityListQueryKey(listQuery);
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      queryClient.setQueryData(key, page);
      void queryClient.invalidateQueries({
        queryKey: communityListQueryKeyPrefix,
        predicate: (query) => hashKey(query.queryKey) !== hashKey(key),
      });
    },
  };
}

export function useSetCommunityHidden() {
  const queryClient = useQueryClient();
  return useMutation(setCommunityHiddenMutationOptions(queryClient));
}

// One community's details (#745), the community page's read (ADR-0014
// decision 2).
//
// - **One key per community**, built by the exported `communityDetailsQueryKey`
//   under an exported prefix: the link verbs (#754) answer with the updated
//   links and write them into this key rather than refetching.
// - **RTK Query's defaults** (`SOURCE_CACHE_LIFETIME`): the source's
//   `getCommunityFalcon` overrides none (`communitiesApi.ts:27-58`), the Read
//   side's cache ruling (#697 D8).
// - **No `keepPreviousData`**: the donor island reads `currentData`, so another
//   community shows skeletons, never the previous one's data.
//
// The island's waiting states (`CommunityDetailsIsland.tsx:50-53`), in
// TanStack's words, are `communityDetailsWaitingState`'s — the page (#751) reads
// that rather than re-deriving them; a `null` result is the 404 state and
// `isError` the generic error state. The `null` is never trusted from the
// cache: a new visit asks again, as the source's error did — the 404 kept up,
// "Refresh Page" spinning, while it does.

export { communityDetailsQueryKey, communityDetailsQueryKeyPrefix };

export const communityDetailsQueryOptions = (communityId: number) =>
  queryOptions({
    queryKey: communityDetailsQueryKey(communityId),
    queryFn: () => communityDetails({ data: { communityId } }),
    ...SOURCE_CACHE_LIFETIME,
    // The source's 404 was an RTK Query *error*, never cached as an answer, so
    // every new visit asked again; an absence is stale at once here, too.
    staleTime: ({ state }) => (state.data === null ? 0 : Infinity),
  });

/**
 * The donor island's two waiting states over the details query —
 * `isLoading || (isFetching && !currentData)` and `isFetching && !isLoading &&
 * !!currentData` (`CDI:51-52`) — read through RTK Query's pending refetch,
 * which **keeps the last `error` and drops `isError`** (`writePendingCacheEntry`,
 * `rtk-query.modern.mjs:1281-1303`), the island checking that error first:
 *
 * - **`"loading"`**, the skeletons — the first load, or a retry after an error
 *   with no community to show (a failed first load, an error over an absence);
 * - **`"refetching"`** — a fetch over a community shown, the 24 px spinner,
 *   even a retry after an error over it; or a re-ask over an absence, which
 *   the page draws as the 404 with "Refresh Page" spinning, the donor's kept
 *   404 error;
 * - `null` otherwise.
 */
export function communityDetailsWaitingState(query: {
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  data: unknown;
}): "loading" | "refetching" | null {
  if (query.isPending || (query.isFetching && query.isError && query.data == null)) {
    return "loading";
  }
  if (query.isFetching) return "refetching";
  return null;
}

/**
 * One community as the details read answers it — `null` when it does not exist.
 * The page's and the coordinate hook's type, which the client graph cannot name
 * from the wire face; the `CommunityMapData` precedent below.
 */
export type CommunityDetails = Awaited<ReturnType<typeof communityDetails>>;

/** One community, or `null` when it does not exist. The page passes the
 *  number `communityIdFromRouteParam` read — one the validator refuses
 *  included — and asks nothing for its `"not-found"`. */
export function useCommunityDetails(communityId: number) {
  return useQuery(communityDetailsQueryOptions(communityId));
}

// One community's map data (#745), every Homesite map mount's read — the
// details page's and the list rail's (#749).
//
// - **Asked only while the map is visible**: the page passes
//   `enabled = enable_community_map && mapVisible`, the donor island's
//   (`CommunityDetailsIsland.tsx:89`), so a hidden map costs no polygon fetch.
// - **Refetched every time it is shown**: the source's `forceRefetch: () => true`
//   (`communitiesApi.ts:91-115`) is `staleTime: 0` — a newly enabled or mounted
//   observer refetches — with RTK Query's 60 s lifetime and no focus or
//   reconnect refetch, so an open map never refetches by itself.

/** What every map-data key starts with. */
export const communityMapDataQueryKeyPrefix = ["communities", "map-data"] as const;

export const communityMapDataQueryKey = (communityId: number) =>
  [...communityMapDataQueryKeyPrefix, communityId] as const;

export const communityMapDataQueryOptions = (communityId: number, enabled: boolean) =>
  queryOptions({
    queryKey: communityMapDataQueryKey(communityId),
    queryFn: () => communityMapData({ data: { communityId } }),
    enabled,
    ...SOURCE_CACHE_LIFETIME,
    staleTime: 0,
  });

/** One community's map data as the read answers it — `null` for an absent
 *  community. The Homesite map's prop type, which the client graph cannot name
 *  from the wire face. */
export type CommunityMapData = Awaited<ReturnType<typeof communityMapData>>;

/** The Homesite map's data, or `null` when the community does not exist. */
export function useCommunityMapData({
  communityId,
  enabled,
}: {
  communityId: number;
  enabled: boolean;
}) {
  return useQuery(communityMapDataQueryOptions(communityId, enabled));
}
